import React from 'react';
import {
  Edit,
  SelectInput,
  TextInput,
  required,
  TabbedForm,
  FormTab,
  useRecordContext,
} from 'react-admin';
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from '../../generated/data-contracts';
import { Box, Typography, Chip } from '@mui/material';

// Componente personalizado para mostrar información de solo lectura
const ReadOnlyInfo = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusColors = {
    [TicketStatus.Open]: 'primary',
    [TicketStatus.InProgress]: 'secondary',
    [TicketStatus.Resolved]: 'success',
    [TicketStatus.Closed]: 'default',
    [TicketStatus.Cancelled]: 'error',
  } as const;

  const priorityColors = {
    [TicketPriority.Low]: 'default',
    [TicketPriority.Medium]: 'primary',
    [TicketPriority.High]: 'warning',
    [TicketPriority.Critical]: 'error',
  } as const;

  const categoryColors = {
    [TicketCategory.Bug]: 'error',
    [TicketCategory.Feature]: 'primary',
    [TicketCategory.Support]: 'info',
    [TicketCategory.Contact]: 'secondary',
    [TicketCategory.General]: 'default',
  } as const;

  const statusLabels = {
    [TicketStatus.Open]: 'Abierto',
    [TicketStatus.InProgress]: 'En Progreso',
    [TicketStatus.Resolved]: 'Resuelto',
    [TicketStatus.Closed]: 'Cerrado',
    [TicketStatus.Cancelled]: 'Cancelado',
  };

  const priorityLabels = {
    [TicketPriority.Low]: 'Baja',
    [TicketPriority.Medium]: 'Media',
    [TicketPriority.High]: 'Alta',
    [TicketPriority.Critical]: 'Crítica',
  };

  const categoryLabels = {
    [TicketCategory.Bug]: 'Error/Bug',
    [TicketCategory.Feature]: 'Nueva Funcionalidad',
    [TicketCategory.Support]: 'Soporte',
    [TicketCategory.Contact]: 'Contacto',
    [TicketCategory.General]: 'General',
  };

  return (
    <Box sx={{ mb: 3, p: 2, borderRadius: 1 }}>
      <Typography variant='h6' gutterBottom>
        Información del Ticket #{record.id}
      </Typography>
      <Box display='flex' gap={2} flexWrap='wrap' alignItems='center'>
        <Box>
          <Typography variant='body2' color='textSecondary'>
            Estado:
          </Typography>
          <Chip
            label={statusLabels[record.status as TicketStatus] || record.status}
            color={statusColors[record.status as TicketStatus] || 'default'}
            size='small'
          />
        </Box>
        <Box>
          <Typography variant='body2' color='textSecondary'>
            Prioridad:
          </Typography>
          <Chip
            label={
              priorityLabels[record.priority as TicketPriority] ||
              record.priority
            }
            color={
              priorityColors[record.priority as TicketPriority] || 'default'
            }
            size='small'
          />
        </Box>
        <Box>
          <Typography variant='body2' color='textSecondary'>
            Categoría:
          </Typography>
          <Chip
            label={
              categoryLabels[record.category as TicketCategory] ||
              record.category
            }
            color={
              categoryColors[record.category as TicketCategory] || 'default'
            }
            size='small'
          />
        </Box>
        <Box>
          <Typography variant='body2' color='textSecondary'>
            Creado: {new Date(record.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        {record.assignedToUser && (
          <Box>
            <Typography variant='body2' color='textSecondary'>
              Asignado a:{' '}
              {record.assignedToUser.fullName || record.assignedToUser.email}
            </Typography>
          </Box>
        )}
        {record.createdByUserName && (
          <Box>
            <Typography variant='body2' color='textSecondary'>
              Creado por:{' '}
              {record.createdByUserFullName || record.createdByUserName}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const TicketsEdit: React.FC = () => (
  <Edit title='Editar Ticket' mutationMode='pessimistic'>
    <TabbedForm>
      <FormTab label='Información General'>
        <ReadOnlyInfo />

        <Box display='flex' flexDirection='column' gap={2}>
          <Typography variant='h6' gutterBottom>
            Información Básica del Ticket
          </Typography>

          <TextInput
            source='title'
            label='Título'
            validate={[required()]}
            fullWidth
          />

          <TextInput
            source='description'
            label='Descripción'
            multiline
            rows={6}
            validate={[required()]}
            fullWidth
          />

          <SelectInput
            source='category'
            label='Categoría'
            choices={[
              { id: TicketCategory.Bug, name: 'Error/Bug' },
              { id: TicketCategory.Feature, name: 'Nueva Funcionalidad' },
              { id: TicketCategory.Support, name: 'Soporte' },
              { id: TicketCategory.Contact, name: 'Contacto' },
              { id: TicketCategory.General, name: 'General' },
            ]}
            validate={[required()]}
          />

          <SelectInput
            source='priority'
            label='Prioridad'
            choices={[
              { id: TicketPriority.Low, name: 'Baja' },
              { id: TicketPriority.Medium, name: 'Media' },
              { id: TicketPriority.High, name: 'Alta' },
              { id: TicketPriority.Critical, name: 'Crítica' },
            ]}
          />
        </Box>
      </FormTab>

      <FormTab label='Estado y Asignación'>
        <ReadOnlyInfo />

        <Box display='flex' flexDirection='column' gap={2}>
          <Typography variant='h6' gutterBottom>
            Gestión del Estado
          </Typography>

          <SelectInput
            source='status'
            label='Estado del Ticket'
            choices={[
              { id: TicketStatus.Open, name: 'Abierto' },
              { id: TicketStatus.InProgress, name: 'En Progreso' },
              { id: TicketStatus.Resolved, name: 'Resuelto' },
              { id: TicketStatus.Closed, name: 'Cerrado' },
              { id: TicketStatus.Cancelled, name: 'Cancelado' },
            ]}
          />

          <TextInput
            source='resolutionNotes'
            label='Notas de Resolución'
            multiline
            rows={4}
            fullWidth
            helperText='Describe la solución aplicada o el motivo del cambio de estado'
          />

          <TextInput
            source='assignedToUserId'
            label='ID Usuario Asignado'
            helperText='ID del usuario al que se asigna el ticket'
          />
        </Box>
      </FormTab>
    </TabbedForm>
  </Edit>
);
